import { useState } from "react";
import { Box, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslate } from "@refinedev/core";

import { DataGrid } from "@shared/components/DataGrid";
import { TableCell } from "@shared/components";
import { usePaginatedTable } from "@shared/hooks";
import { PartnerTier } from "@modules/partners/types";
import { TierFormModal } from "@modules/partners/components";
import { useDeleteTierMutation } from "@modules/partners/services/mutations";
import { formatCurrency } from "@core/utils/masks";

export function TiersTab() {
  const translate = useTranslate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PartnerTier | null>(null);

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<PartnerTier>({
    resource: "v1/partner_tier_admin",
    sorters: { ordering: "rank" },
  });

  const deleteMutation = useDeleteTierMutation();

  const handleDelete = async (id: number) => {
    if (!window.confirm(translate("v1/partner_tier_admin.confirm_delete"))) return;
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: translate("v1/partner_tier_admin.fields.name"),
      minWidth: 200,
      renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
    },
    {
      field: "rank",
      headerName: translate("v1/partner_tier_admin.fields.rank"),
      minWidth: 100,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "min_sales_value",
      headerName: translate("v1/partner_tier_admin.fields.min_sales_value"),
      minWidth: 160,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "commission_percentage",
      headerName: translate("v1/partner_tier_admin.fields.commission_percentage"),
      minWidth: 140,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "actions",
      headerName: translate("table.actions"),
      minWidth: 120,
      sortable: false,
      display: "flex",
      renderCell: ({ row }) => (
        <TableCell.Container align="center">
          <IconButton
            size="small"
            onClick={() => {
              setEditing(row as PartnerTier);
              setModalOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell.Container>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          variant="contained"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          {translate("v1/partner_tier_admin.titles.create")}
        </Button>
      </Box>

      <DataGrid {...tableProps} rows={rows} columns={columns} loading={loading} />

      <TierFormModal
        open={modalOpen}
        tier={editing}
        onClose={() => {
          setModalOpen(false);
          refetch();
        }}
      />
    </>
  );
}
